/**
 * Collections Dashboard - Enhancement Features
 * Additional error handling, loading states, and polish features
 */

import { showToast } from '../collections/CollectionUI.js';
import { DASHBOARD_CONFIG } from './config.js';

export class DashboardEnhancements {
    constructor(dashboard) {
        this.dashboard = dashboard;
        this.initializeEnhancements();
    }
    
    initializeEnhancements() {
        this.setupGlobalErrorHandler();
        this.setupNetworkMonitoring();
        this.setupLoadingStateEnhancements();
        this.setupKeyboardShortcuts();
        this.setupAccessibilityFeatures();
    }
    
    setupGlobalErrorHandler() {
        // Catch unhandled errors in dashboard operations
        window.addEventListener('error', (event) => {
            if (event.filename && event.filename.includes('collections-dashboard')) {
                this.handleDashboardError(event.error);
            }
        });
        
        window.addEventListener('unhandledrejection', (event) => {
            if (event.reason && this.isCollectionDashboardError(event.reason)) {
                this.handleDashboardError(event.reason);
                event.preventDefault();
            }
        });
    }
    
    setupNetworkMonitoring() {
        // Monitor network status for better error handling
        window.addEventListener('online', () => {
            showToast('Kết nối mạng đã được khôi phục', 'success');
            
            // Retry failed operations if any
            if (this.dashboard.isLoading === false && this.dashboard.collections.length === 0) {
                setTimeout(() => {
                    this.dashboard.loadCollections();
                }, 1000);
            }
        });
        
        window.addEventListener('offline', () => {
            showToast('Mất kết nối mạng. Một số tính năng có thể không hoạt động.', 'warning');
        });
    }
    
    setupLoadingStateEnhancements() {
        // Add enhanced loading indicators
        this.addProgressiveLoading();
        this.addSmartRetry();
    }
    
    addProgressiveLoading() {
        // Add shimmer effect to skeleton loading
        const style = document.createElement('style');
        style.textContent = `
            @keyframes shimmer {
                0% {
                    background-position: -200px 0;
                }
                100% {
                    background-position: calc(200px + 100%) 0;
                }
            }
            
            .shimmer-effect {
                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                background-size: 200px 100%;
                animation: shimmer 1.5s infinite;
            }
            
            .collections-loading .animate-pulse {
                animation: none;
            }
            
            .collections-loading .bg-gray-200 {
                background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
                background-size: 200px 100%;
                animation: shimmer 1.5s infinite;
            }
        `;
        document.head.appendChild(style);
    }
    
    addSmartRetry() {
        // Add exponential backoff for failed requests
        let retryCount = 0;
        const maxRetries = 3;
        
        const originalLoadCollections = this.dashboard.loadCollections.bind(this.dashboard);
        
        this.dashboard.loadCollections = async (...args) => {
            try {
                await originalLoadCollections(...args);
                retryCount = 0; // Reset on success
            } catch (error) {
                retryCount++;
                
                if (retryCount <= maxRetries) {
                    const delay = Math.pow(2, retryCount - 1) * 1000; // 1s, 2s, 4s
                    
                    showToast(`Thử lại lần ${retryCount}/${maxRetries} sau ${delay/1000}s...`, 'info');
                    
                    setTimeout(() => {
                        this.dashboard.loadCollections(...args);
                    }, delay);
                } else {
                    retryCount = 0;
                    throw error;
                }
            }
        };
    }
    
    setupKeyboardShortcuts() {
        document.addEventListener('keydown', (e) => {
            // Only activate shortcuts when dashboard is visible
            if (!document.querySelector('.collections-dashboard')) return;
            
            // Create collection: Ctrl/Cmd + N
            if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
                e.preventDefault();
                this.dashboard.showCreateModal();
            }
            
            // Refresh: Ctrl/Cmd + R or F5
            if (((e.ctrlKey || e.metaKey) && e.key === 'r') || e.key === 'F5') {
                e.preventDefault();
                this.dashboard.loadCollections();
                showToast('Đã làm mới danh sách collections', 'info');
            }
            
            // Search: Ctrl/Cmd + F
            if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
                e.preventDefault();
                const searchInput = document.getElementById('collections-search');
                if (searchInput) {
                    searchInput.focus();
                    searchInput.select();
                }
            }
            
            // Select all: Ctrl/Cmd + A (when not in input)
            if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !e.target.matches('input, textarea')) {
                e.preventDefault();
                const selectAllCheckbox = document.getElementById('select-all-collections');
                if (selectAllCheckbox) {
                    selectAllCheckbox.checked = true;
                    selectAllCheckbox.dispatchEvent(new Event('change'));
                }
            }
        });
    }
    
    setupAccessibilityFeatures() {
        // Add ARIA live regions for screen readers
        this.addAriaLiveRegions();
        
        // Improve focus management
        this.improveFocusManagement();
        
        // Add keyboard navigation for dropdowns
        this.addDropdownKeyboardNavigation();
    }
    
    addAriaLiveRegions() {
        const dashboard = document.querySelector('.collections-dashboard');
        if (!dashboard) return;
        
        // Create status region for loading/error announcements
        const statusRegion = document.createElement('div');
        statusRegion.setAttribute('aria-live', 'polite');
        statusRegion.setAttribute('aria-atomic', 'true');
        statusRegion.className = 'sr-only';
        statusRegion.id = 'dashboard-status';
        dashboard.appendChild(statusRegion);
        
        // Create count region for collection counts
        const countRegion = document.createElement('div');
        countRegion.setAttribute('aria-live', 'polite');
        countRegion.className = 'sr-only';
        countRegion.id = 'collection-count-status';
        dashboard.appendChild(countRegion);
        
        // Monitor collection changes
        const observer = new MutationObserver(() => {
            const collectionItems = document.querySelectorAll('.collection-item');
            const count = collectionItems.length;
            countRegion.textContent = `${count} collections được hiển thị`;
        });
        
        const listContainer = document.getElementById('collections-list-container');
        if (listContainer) {
            observer.observe(listContainer, { childList: true });
        }
    }
    
    improveFocusManagement() {
        // Focus management for modals
        const createModal = document.getElementById('create-collection-modal');
        if (createModal) {
            createModal.addEventListener('transitionend', (e) => {
                if (e.target === createModal && !createModal.classList.contains('hidden')) {
                    const firstInput = createModal.querySelector('input[type="text"]');
                    if (firstInput) {
                        firstInput.focus();
                    }
                }
            });
        }
        
        // Focus return after modal close
        let lastFocusedElement = null;
        
        document.addEventListener('click', (e) => {
            if (e.target.id === 'create-collection-btn' || e.target.id === 'create-first-collection-btn') {
                lastFocusedElement = e.target;
            }
        });
        
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && createModal && !createModal.classList.contains('hidden')) {
                if (lastFocusedElement) {
                    lastFocusedElement.focus();
                }
            }
        });
    }
    
    addDropdownKeyboardNavigation() {
        // Add arrow key navigation to filter dropdowns
        const dropdowns = ['tags-filter-menu', 'visibility-filter-menu', 'sort-filter-menu'];
        
        dropdowns.forEach(dropdownId => {
            const dropdown = document.getElementById(dropdownId);
            if (!dropdown) return;
            
            dropdown.addEventListener('keydown', (e) => {
                const options = dropdown.querySelectorAll('button');
                const currentIndex = Array.from(options).findIndex(opt => opt === document.activeElement);
                
                switch (e.key) {
                    case 'ArrowDown':
                        e.preventDefault();
                        const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
                        options[nextIndex]?.focus();
                        break;
                        
                    case 'ArrowUp':
                        e.preventDefault();
                        const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
                        options[prevIndex]?.focus();
                        break;
                        
                    case 'Enter':
                    case ' ':
                        e.preventDefault();
                        document.activeElement?.click();
                        break;
                        
                    case 'Escape':
                        e.preventDefault();
                        dropdown.classList.add('hidden');
                        // Focus back to trigger button
                        const triggerButton = document.getElementById(dropdownId.replace('-menu', '-btn'));
                        triggerButton?.focus();
                        break;
                }
            });
        });
    }
    
    handleDashboardError(error) {
        console.error('Dashboard error:', error);
        
        const statusRegion = document.getElementById('dashboard-status');
        if (statusRegion) {
            statusRegion.textContent = 'Đã xảy ra lỗi khi tải dashboard. Vui lòng thử lại.';
        }
        
        // Show user-friendly error message
        if (error.message?.includes('network') || error.message?.includes('fetch')) {
            showToast('Lỗi kết nối mạng. Kiểm tra kết nối và thử lại.', 'error');
        } else if (error.message?.includes('permission') || error.message?.includes('forbidden')) {
            showToast('Không có quyền truy cập. Vui lòng đăng nhập lại.', 'error');
        } else {
            showToast('Đã xảy ra lỗi không mong đợi. Vui lòng thử lại sau.', 'error');
        }
    }
    
    isCollectionDashboardError(error) {
        if (!error) return false;
        
        // Check if error is related to collections dashboard
        const errorString = error.toString().toLowerCase();
        return errorString.includes('collection') || 
               errorString.includes('dashboard') ||
               (error.stack && error.stack.includes('collections-dashboard'));
    }
    
    // Collection size warning feature
    static checkCollectionSizeWarnings(collections) {
        const maxItems = parseInt(window.iphubCollections?.maxItemsPerCollection || DASHBOARD_CONFIG.MAX_ITEMS_PER_COLLECTION, 10);
        const warningThreshold = Math.floor(maxItems * DASHBOARD_CONFIG.SIZE_WARNING_THRESHOLD);
        
        collections.forEach(collection => {
            const itemCount = collection.item_count || 0;
            const element = document.querySelector(`[data-collection-id="${collection.id}"] .collection-warning`);
            
            if (element) {
                if (itemCount >= warningThreshold) {
                    element.classList.remove('hidden');
                    const percentage = Math.round((itemCount / maxItems) * 100);
                    const warningText = element.querySelector('.warning-text');
                    if (warningText) {
                        warningText.textContent = `Collection đã sử dụng ${percentage}% dung lượng (${itemCount}/${maxItems})`;
                    }
                } else {
                    element.classList.add('hidden');
                }
            }
        });
    }
}

// Auto-initialize with dashboard
export function initDashboardEnhancements(dashboard) {
    return new DashboardEnhancements(dashboard);
}
